// client/src/components/pages/LexiStar/NotificationRules/RecipientGroups.jsx
//
// Named recipient groups (FR-NTF-002, e.g. "Mine A control room"): users,
// permission holders, external contacts and Telegram groups, referenced by
// rules as one recipient.

import React, { useState } from 'react';
import { FaPlus, FaUsers } from 'react-icons/fa';
import { EmptyState, inputClasses, cardClasses } from '../StatusBoard/atoms';
import RecipientsEditor from './RecipientsEditor';
import { createGroup, updateGroup } from './notificationsApi';
import { GROUP_MEMBER_KINDS, recipientKindLabel } from './ruleLogic';

const apiError = (err, fallback) => {
  const data = err?.response?.data || {};
  const details = Array.isArray(data.details) ? data.details.map((d) => d.message || d) : [];
  return [data.error, ...details].filter(Boolean).join(' — ') || fallback;
};

function GroupEditor({ group, users, onSaved, onCancel }) {
  const [name, setName] = useState(group?.name || '');
  const [members, setMembers] = useState(group?.members || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    const clean = members.map((m) => ({
      kind: m.kind,
      ref: m.kind === 'user' ? Number(m.ref) : String(m.ref ?? '').trim(),
      ...(m.name ? { name: m.name } : {}),
      ...(m.channel ? { channel: m.channel } : {}),
    }));
    try {
      const res = group?.id
        ? await updateGroup(group.id, {
            name: name.trim(),
            members: clean,
            ...(group.version ? { version: group.version } : {}),
          })
        : await createGroup({ name: name.trim(), members: clean });
      onSaved(res?.group || res);
    } catch (err) {
      setError(apiError(err, 'The group was not saved'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3"
      role="group"
      aria-label={group?.id ? `Edit ${group.name}` : 'New group'}
    >
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      <div>
        <label htmlFor="group-name" className="block text-xs font-medium text-slate-600 mb-1">
          Group name
        </label>
        <input
          id="group-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputClasses}
          placeholder="Mine A control room"
        />
      </div>
      <RecipientsEditor
        value={members}
        onChange={setMembers}
        kinds={GROUP_MEMBER_KINDS}
        users={users}
        idPrefix="grp"
        label="Members"
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!name.trim() || saving}
          className="px-4 py-1.5 text-sm font-semibold text-white rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-slate-400"
        >
          {saving ? 'Saving...' : 'Save group'}
        </button>
      </div>
    </div>
  );
}

const memberText = (m) =>
  m.kind === 'role'
    ? `everyone with ${m.ref}`
    : m.name || `${recipientKindLabel(m.kind).toLowerCase()} ${m.ref}`;

export default function RecipientGroups({ groups, users, canManage, onChanged }) {
  const [editing, setEditing] = useState(null);
  return (
    <section className={cardClasses} aria-label="Recipient groups">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <p className="text-sm text-slate-600">Groups a rule can notify as one recipient.</p>
        {canManage && editing === null && (
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg"
          >
            <FaPlus size={10} /> New group
          </button>
        )}
      </div>
      {editing === 'new' && (
        <div className="p-4">
          <GroupEditor
            users={users}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              onChanged();
            }}
          />
        </div>
      )}
      {groups.length === 0 && editing !== 'new' ? (
        <EmptyState icon={FaUsers} title="No recipient groups yet">
          Group the people who share a duty — a control room, the night shift, a customer's
          contacts.
        </EmptyState>
      ) : (
        <ul className="divide-y divide-slate-100">
          {groups.map((g) => (
            <li key={g.id} className="px-4 py-3">
              {editing === g.id ? (
                <GroupEditor
                  group={g}
                  users={users}
                  onCancel={() => setEditing(null)}
                  onSaved={() => {
                    setEditing(null);
                    onChanged();
                  }}
                />
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">{g.name}</p>
                    <p className="text-sm text-slate-600">
                      {(g.members || []).length
                        ? (g.members || []).map(memberText).join(' · ')
                        : 'No members'}
                    </p>
                  </div>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => setEditing(g.id)}
                      className="text-sm font-medium text-primary-700 hover:underline"
                    >
                      Edit
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
